import React, { useState, useEffect } from 'react';
import './QuestionCard.css';

const QuestionCard = ({ 
  question, 
  questionIndex, 
  totalQuestions,
  selectedAnswer, 
  onAnswerSelect,
  isReviewMode = false,
  correctAnswer = null,
  shuffleChoices = true
}) => {
  const [shuffledChoices, setShuffledChoices] = useState([]);

  useEffect(() => {
    if (question?.choices) {
      if (shuffleChoices && !isReviewMode) {
        // Shuffle choices for exam integrity
        const choices = [...question.choices];
        for (let i = choices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [choices[i], choices[j]] = [choices[j], choices[i]];
        }
        setShuffledChoices(choices);
      } else {
        setShuffledChoices(question.choices);
      }
    }
  }, [question, shuffleChoices, isReviewMode]);

  if (!question) {
    return (
      <div className="question-card loading">
        <div className="loading-skeleton">
          <div className="skeleton-line title"></div>
          <div className="skeleton-line"></div>
          <div className="skeleton-line"></div>
          <div className="skeleton-choices">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton-choice"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const getChoiceClass = (choice) => {
    let className = 'choice';
    
    if (isReviewMode && correctAnswer) {
      if (choice === correctAnswer) {
        className += ' correct';
      } else if (choice === selectedAnswer && choice !== correctAnswer) {
        className += ' incorrect';
      }
    } else if (selectedAnswer === choice) {
      className += ' selected';
    }
    
    return className;
  };

  const getChoiceIcon = (choice) => {
    if (isReviewMode && correctAnswer) {
      if (choice === correctAnswer) {
        return '✓';
      } else if (choice === selectedAnswer && choice !== correctAnswer) {
        return '✗';
      }
    }
    return '';
  };

  return (
    <div className="question-card">
      <div className="question-header">
        <div className="question-meta">
          <span className="question-number">Question {questionIndex + 1}</span>
          {question.category && (
            <span className="question-category">{question.category}</span>
          )}
        </div>
        <div className="question-progress">
          {questionIndex + 1} / {totalQuestions}
        </div>
      </div>
      
      <div className="question-content">
        <h3 className="question-text">{question.question}</h3>
        
        <div className="choices-container">
          <div className="choices-instruction">
            {isReviewMode ? 'Review your answer:' : 'Select your answer:'}
          </div>
          
          <div className="choices">
            {shuffledChoices.map((choice, index) => (
              <div key={`${choice}-${index}`} className={getChoiceClass(choice)}>
                <label className="choice-label">
                  <input
                    type="radio"
                    name={`question-${questionIndex}`}
                    value={choice}
                    checked={selectedAnswer === choice}
                    onChange={() => !isReviewMode && onAnswerSelect(choice)}
                    disabled={isReviewMode}
                    className="choice-input"
                  />
                  <span className="choice-indicator">
                    <span className="choice-icon">{getChoiceIcon(choice)}</span>
                  </span>
                  <span className="choice-text">{choice}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
        
        {isReviewMode && (
          <div className="review-info">
            <div className="answer-status">
              {selectedAnswer === correctAnswer ? (
                <span className="status correct">✓ Correct</span>
              ) : (
                <span className="status incorrect">✗ Incorrect</span>
              )}
            </div>
            {selectedAnswer !== correctAnswer && (
              <div className="correct-answer-info">
                <strong>Correct answer:</strong> {correctAnswer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;